"use client";
import React, { useEffect, useState } from "react";
import { PageTextOnlyLPOldDesignSchema } from "@/sanity/pages/PageTextOnlyLPOldDesignTemplate";
import Footer from "@/components/global/partials/Footer";
import HeroSection from "../pageComponents/text-only-old-design/Hero";
import TextWithCertificateSection from "../pageComponents/text-only-old-design/TextWithCertificate";
import ThisPageSection from "../pageComponents/text-only-old-design/ThisPage";
import EmergedSection from "../pageComponents/text-only-old-design/Emerged";
import bg from "@/public/home/section-bg.jpg";
import Image from "@/helpers/Image";
import Wins from "../pageComponents/text-only-old-design/Wins";
import PropTrading from "../pageComponents/text-only-old-design/PropTrading";
import LastCTASection from "../global/sections/LastCTA";
import { cn } from "@/lib/utils";
import HistorySection from "../pageComponents/text-only-old-design/HistorySection";
import logo from "@/public/logo-black.svg";
import { Motion } from "../global/utils/Motion";
import { resolveLink } from "@/sanity/globals/CTA";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";

export default function PageTextOnlyLPOldDesignJSX({ data }: { data: PageTextOnlyLPOldDesignSchema }) {
  const lang = data?.settings?.language || DEFAULT_LANGUAGE;
  const homeHref = resolveLink("/", lang);
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!homeHref) return;
    setFinalLink(homeHref);
  }, [homeHref]);

  return (
    <main className="prose-p:mb-[2rem] prose-headings:mb-[1.375rem] prose-headings:font-[600] prose-headings:tracking-[-0.03em]">
      <nav
        className={cn(
          "flex absolute z-40 justify-center items-end w-full lg:px-0 top-[4rem] start-0 px-[4rem] lg:top-[9rem]",
        )}>
        <Motion
          className="relative mx-auto flex w-full max-w-[187.375rem] items-center justify-center py-[1.5rem] lg:py-0"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}>
          <a href={finalLink}>
            <Image
              alt="Logo"
              src={logo}
              className="w-[16.375rem]"
            />
          </a>
        </Motion>
      </nav>

      <HeroSection data={data} />

      <div className={cn("relative overflow-clip py-[5rem] lg:py-[7rem] px-[2rem] lg:px-0")}>
        <div className="absolute top-0 left-1/2 hidden h-full w-[38.25rem] -translate-x-1/2 justify-between opacity-50 sm:w-[55rem] lg:flex lg:w-[min(180rem,80%)] lg:opacity-100">
          {Array.from({ length: 7 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                "h-full border-l border-l-[#E5E5E5]",
                index >= 2 && index < 5 && "border-l-transparent",
                index >= 5 && "hidden lg:block",
              )}
            />
          ))}
        </div>
        <div className="absolute top-0 left-1/2 flex h-full w-[49.25rem] -translate-x-1/2 justify-between sm:w-[55rem] lg:hidden lg:w-[min(180rem,80%)]">
          {Array.from({ length: 2 }).map((_, index) => (
            <div
              key={index}
              className={cn("h-full border-l border-l-[#E5E5E5]")}
            />
          ))}
        </div>
        <div className="relative mx-auto flex w-[min(94.375rem,100%)] flex-col items-start">
          <HistorySection data={data} />
          <TextWithCertificateSection data={data} />

          <div
            className="relative w-full"
            style={{ overflowAnchor: "none" }}>
            <div className="relative mx-auto w-[min(229.25rem,100%)] overflow-clip rounded-[2rem] lg:rounded-[5rem]">
              <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-[#E6EDE9]">
                <Image
                  className="absolute bottom-0 left-1/2 !h-auto !w-[max(100%,120rem)] !max-w-none -translate-x-1/2 object-cover opacity-[.10] mix-blend-luminosity"
                  style={{
                    maskImage: "linear-gradient(to top, black 20%, transparent 100%)",
                    WebkitMaskImage: "linear-gradient(to top, black 20%, transparent 100%)",
                  }}
                  src={bg}
                  quality={100}
                  alt="background art"
                />
              </div>
              <Wins data={data} />
            </div>
          </div>
          <ThisPageSection data={data} />

          <div
            className="relative w-full"
            style={{ overflowAnchor: "none" }}>
            <div className="relative mx-auto w-[min(229.25rem,100%)] overflow-clip rounded-[2rem] lg:rounded-[5rem]">
              <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-[#103037]">
                <Image
                  className="absolute bottom-0 left-1/2 !h-auto !w-[max(100%,120rem)] !max-w-none -translate-x-1/2 object-cover opacity-[.05] mix-blend-luminosity"
                  style={{
                    maskImage: "linear-gradient(to top, black 20%, transparent 100%)",
                    WebkitMaskImage: "linear-gradient(to top, black 20%, transparent 100%)",
                  }}
                  src={bg}
                  quality={100}
                  alt="background art"
                />
              </div>
              <PropTrading data={data} />
            </div>
          </div>

          <EmergedSection data={data} />
        </div>
      </div>

      <LastCTASection
        chip={data?.lastChip}
        text={data?.lastText}
        cta={data?.lastCTA}
        textClassName="w-[min(80rem,90%)]"
        ctaClassName="mb-[14rem] lg:mb-0"
      />
      {data?.settings?.footer && (
        <Footer
          data={data.settings.footer}
          language={data?.settings?.language}
          hideFinalCta
        />
      )}
    </main>
  );
}
